import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { getAppointmentOrNull } from '../../api/appointments';
import { markCompanyUpdateRead, markCompanyUpdatesRead } from '../../api/updates/updates';
import { CardCloseIcon } from './icons/OverviewCardIcons';
import AppointmentStatusPill from './AppointmentStatusPill';
import OverviewBoardHeader from './board/OverviewBoardHeader';
import { ListRowSkeletonStack } from '../loading/skeletons';
import './styles/RecentUpdatesCard.css';
import './styles/TodayAppointmentsCard.css';
import './board/OverviewBoard.css';

const RecentUpdatesCard = ({ updates = [], loading = false, companyId, onUpdateRead, onClearAllRead }) => {
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isDialogAnimating, setIsDialogAnimating] = useState(false);
  const [isResolvingService, setIsResolvingService] = useState(false);

  useEffect(() => {
    if (isDetailsDialogOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isDetailsDialogOpen]);

  const getUpdateColor = (type) => {
    const normalizedType = type?.toLowerCase()
      .replace('appointment_', '')
      .replace('client_', '');
    
    switch (normalizedType) {
      case 'confirmed':
        return '#4caf50';
      case 'cancelled':
        return '#f44336';
      case 'rescheduled':
        return '#ff9800';
      case 'no_show':
      case 'noshow':
      case 'no show':
        return '#9c27b0';
      default:
        console.log('Unmatched update type:', type, 'normalized:', normalizedType);
        return '#2196f3';
    }
  };

  const getUpdateMessage = (update) => {
    if (update.message) return update.message;
    
    const clientName = update.details?.clientName || 'client';
    const normalizedType = String(update.type || '')
      .replace(/^appointment_/i, '')
      .toLowerCase()
      .replaceAll('_', ' ');
    const actor = update.updatedBy || update.details?.updatedBy;
    const actorDisplay = getActorDisplayName(update, actor);
    
    switch (normalizedType) {
      case 'confirmed':
        return `${actorDisplay} confirmed appointment for ${clientName}`;
      case 'cancelled':
        return `${actorDisplay} cancelled appointment for ${clientName}`;
      case 'archived':
        return `${actorDisplay} archived appointment for ${clientName}`;
      case 'rescheduled':
        return `${actorDisplay} rescheduled appointment for ${clientName}`;
      case 'no show':
        return `No-show recorded for ${clientName}`;
      case 'client confirmed':
        return `${clientName} confirmed their appointment`;
      case 'client cancelled':
        return `${clientName} cancelled their appointment`;
      case 'client rescheduled':
        return `${clientName} requested to reschedule their appointment`;
      default:
        return `Appointment updated for ${clientName}`;
    }
  };

  const getUpdatePill = (update) => {
    const type = String(update.type || '').toLowerCase();
    if (type.includes('cancel')) return { status: 'cancelled', label: 'cancelled' };
    if (type.includes('no_show') || type.includes('no show') || type.includes('noshow')) {
      return { status: 'no show', label: 'no show' };
    }
    if (type.includes('reschedul')) return { status: 'rescheduled', label: 'rescheduled' };
    if (type.includes('confirm')) return { status: 'confirmed', label: 'confirmed' };
    if (type.includes('complet')) return { status: 'completed', label: 'completed' };
    if (type.includes('created')) return { status: 'pending', label: 'created' };
    return { status: 'pending', label: 'updated' };
  };

  const getActorDisplayName = (update, actor) => {
    const actorNameCandidates = [
      update?.updatedByName,
      update?.details?.updatedByName,
      update?.updatedByDisplayName,
      update?.details?.updatedByDisplayName
    ];

    const actorName = actorNameCandidates.find(
      (candidate) => typeof candidate === 'string' && candidate.trim()
    );

    if (actorName) return actorName.trim();

    if (!actor) return 'System';

    if (actor === 'client') return 'Client';
    return 'Employee';
  };

  const handleCheckboxClick = async (update) => {
    if (!companyId || update.read) return;

    try {
      await markCompanyUpdateRead({ companyId, id: update.id });
      if (onUpdateRead) {
        onUpdateRead(update.id);
      }
    } catch (error) {
      console.error('Error marking update as read:', error);
    }
  };

  const handleClearAllClick = async () => {
    if (!companyId || unreadUpdates.length === 0) return;

    try {
      const unreadIds = unreadUpdates.map((update) => update.id).filter(Boolean);
      await markCompanyUpdatesRead({ companyId, ids: unreadIds });

      if (onClearAllRead) {
        onClearAllRead(unreadIds);
      } else if (onUpdateRead) {
        unreadIds.forEach((updateId) => onUpdateRead(updateId));
      }
    } catch (error) {
      console.error('Error clearing unread updates:', error);
    }
  };

  const getServiceFromAppointmentDoc = (appointment) => {
    if (!appointment) return '';

    if (appointment.serviceName && typeof appointment.serviceName === 'string' && appointment.serviceName.trim()) {
      return appointment.serviceName.trim();
    }

    if (appointment.serviceTags && appointment.serviceTags.length > 0) {
      const firstTag = appointment.serviceTags[0];
      if (typeof firstTag === 'string' && firstTag.trim()) {
        return firstTag.trim();
      }
      if (typeof firstTag === 'object' && typeof firstTag.name === 'string' && firstTag.name.trim()) {
        return firstTag.name.trim();
      }
    }

    if (appointment.service) {
      if (typeof appointment.service === 'string' && appointment.service.trim()) {
        return appointment.service.trim();
      }
      if (
        typeof appointment.service === 'object' &&
        typeof appointment.service.name === 'string' &&
        appointment.service.name.trim()
      ) {
        return appointment.service.name.trim();
      }
    }

    return '';
  };

  const fetchServiceFromAppointmentId = async (appointmentId) => {
    if (!companyId || !appointmentId) return '';

    try {
      const appointment = await getAppointmentOrNull({ companyId, id: appointmentId });
      return getServiceFromAppointmentDoc(appointment);
    } catch {
      /* ignore */
    }

    return '';
  };

  const handleUpdateClick = async (update) => {
    setSelectedUpdate(update);
    setIsDialogAnimating(true);
    // Defer showing dialog to next frame so overlay paints first and reduces GPU jank
    requestAnimationFrame(() => {
      setIsDetailsDialogOpen(true);
    });

    // Optionally resolve service from appointment doc when not in details
    if (getServiceDisplay(update) === 'N/A' && update?.appointmentId) {
      try {
        setIsResolvingService(true);
        const resolvedService = await fetchServiceFromAppointmentId(
          update.appointmentId
        );

        if (resolvedService) {
          setSelectedUpdate((prev) => {
            if (!prev || prev.id !== update.id) return prev;
            return {
              ...prev,
              details: {
                ...prev.details,
                service: resolvedService
              }
            };
          });
        }
      } catch (error) {
        console.error('Error resolving service for update:', error);
      } finally {
        setIsResolvingService(false);
      }
    }
  };

  const getDetailsDialogTitle = () => {
    const type = selectedUpdate?.type?.toLowerCase() || '';
    if (type.includes('cancelled') || type.includes('archived')) return 'Cancellation Details';
    if (type.includes('rescheduled')) return 'Reschedule Details';
    if (type.includes('confirmed')) return 'Confirmation Details';
    if (type.includes('no_show') || type.includes('no show')) return 'No-Show Details';
    return 'Update Details';
  };

  const getDetailsDialogTitleColor = () => {
    const type = selectedUpdate?.type?.toLowerCase() || '';
    if (type.includes('cancelled') || type.includes('archived')) return '#f44336';
    if (type.includes('rescheduled')) return '#ff9800';
    if (type.includes('confirmed')) return '#4caf50';
    if (type.includes('no_show') || type.includes('no show')) return '#9c27b0';
    return '#2196f3';
  };

  const isCancellationType = () => {
    const type = selectedUpdate?.type?.toLowerCase() || '';
    return type.includes('cancelled') || type.includes('archived');
  };

  const getWhatChangedSummary = () => {
    const type = selectedUpdate?.type?.toLowerCase() || '';
    const details = selectedUpdate?.details || {};
    const hasPreviousDateTime = details.previousDateTime != null;

    if (type.includes('rescheduled') || hasPreviousDateTime) {
      return { type: 'date_time', label: 'Date & time was updated' };
    }
    if (type.includes('confirmed')) {
      return { type: 'status', label: 'Status was updated to Confirmed' };
    }
    if (type.includes('cancelled') || type.includes('archived')) {
      return { type: 'cancelled', label: 'Appointment was cancelled' };
    }
    if (type.includes('no_show') || type.includes('no show')) {
      return { type: 'no_show', label: 'Appointment was marked as no-show' };
    }
    return { type: 'updated', label: 'Appointment was updated' };
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    
    try {
      // Handle Firestore Timestamp
      if (dateTime?.toDate) {
        return format(dateTime.toDate(), 'MMM d, yyyy h:mm a');
      }
      
      // Handle Date object
      if (dateTime instanceof Date) {
        return format(dateTime, 'MMM d, yyyy h:mm a');
      }
      
      // Handle string or number timestamp
      const date = new Date(dateTime);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error, dateTime);
      return 'Invalid Date';
    }
  };

  const getServiceDisplay = (update) => {
    const details = update?.details || {};

    const candidates = [
      details.service,
      details.serviceName,
      details.selectedService,
      details.selectedServiceName,
      details.serviceTags?.[0],
      update?.service,
      update?.serviceName
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;

      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }

      if (typeof candidate === 'object') {
        const objectName =
          candidate.name ||
          candidate.serviceName ||
          candidate.title ||
          candidate.label;

        if (typeof objectName === 'string' && objectName.trim()) {
          return objectName.trim();
        }
      }
    }

    return 'N/A';
  };

  // Filter out read updates and sort by timestamp
  const unreadUpdates = updates
    .filter(update => !update.read)
    .sort((a, b) => {
      try {
        const getTime = (timestamp) => {
          if (timestamp?.toDate) return timestamp.toDate().getTime();
          if (timestamp instanceof Date) return timestamp.getTime();
          const date = new Date(timestamp);
          return isNaN(date.getTime()) ? 0 : date.getTime();
        };

        const aTime = getTime(a.timestamp);
        const bTime = getTime(b.timestamp);
        return bTime - aTime;
      } catch (error) {
        console.error('Error sorting updates:', error);
        return 0;
      }
    });

  return (
    <div className="recent-updates-container overview-board">
      <OverviewBoardHeader
        eyebrow="Recent"
        title="Updates"
        count={loading ? 'Loading updates' : `${unreadUpdates.length} unread`}
        actionLabel={unreadUpdates.length > 0 ? 'Clear' : undefined}
        onAction={unreadUpdates.length > 0 ? handleClearAllClick : undefined}
        actionTitle="Mark all updates as read"
      />

      {/* Content Section */}
      <div className="recent-updates-content overview-board__body">
        {loading ? (
          <ListRowSkeletonStack rows={4} variant="compact" label="Loading recent updates" />
        ) : unreadUpdates.length > 0 ? (
          <ul className="today-label-table">
            {unreadUpdates.slice(0, 4).map((update, index) => {
              const when = update.timestamp instanceof Date
                ? update.timestamp
                : new Date(update.timestamp);
              const timeLabel = Number.isNaN(when.getTime()) ? '—' : format(when, 'h:mm');
              const periodLabel = Number.isNaN(when.getTime()) ? '' : format(when, 'a');
              const pill = getUpdatePill(update);

              return (
                <li key={update.id || index} className="today-label-table__item">
                  <div
                    className="today-label-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleUpdateClick(update)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleUpdateClick(update);
                      }
                    }}
                  >
                    <div className="today-label-time">
                      <span className="today-label-time__clock">{timeLabel}</span>
                      {periodLabel ? <span className="today-label-time__period">{periodLabel}</span> : null}
                    </div>
                    <div className="today-label-body">
                      <div className="today-label-body__top">
                        <span className="today-label-name today-label-name--wrap">{getUpdateMessage(update)}</span>
                        <AppointmentStatusPill status={pill.status} label={pill.label} />
                      </div>
                      <span className="recent-update-read">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleCheckboxClick(update);
                          }}
                        >
                          Mark read
                        </button>
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="recent-updates-empty">
            <p>No unread updates</p>
          </div>
        )}
      </div>

      {/* Update Details Modal — HTML/CSS */}
      <div
        className={`update-details-overlay ${isDetailsDialogOpen ? 'is-open' : ''}`}
        onClick={(e) => e.target === e.currentTarget && setIsDetailsDialogOpen(false)}
        onTransitionEnd={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.propertyName !== 'opacity') return;
          if (!isDetailsDialogOpen) {
            setSelectedUpdate(null);
            setIsResolvingService(false);
            setIsDialogAnimating(false);
          }
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="update-details-title"
        aria-hidden={!isDetailsDialogOpen}
      >
        <div
          className={`update-details-dialog ${isDialogAnimating ? 'update-details-dialog--animating' : ''}`}
          onClick={(e) => e.stopPropagation()}
          onTransitionEnd={() => isDetailsDialogOpen && setIsDialogAnimating(false)}
        >
          <header className="update-details-dialog__header">
            <h2
              id="update-details-title"
              className="update-details-dialog__title"
              style={{ color: getDetailsDialogTitleColor() }}
            >
              {getDetailsDialogTitle()}
            </h2>
            <button
              type="button"
              className="update-details-dialog__close"
              onClick={() => setIsDetailsDialogOpen(false)}
              aria-label="Close"
            >
              <CardCloseIcon />
            </button>
          </header>

          <div className="update-details-dialog__body">
            {selectedUpdate && (() => {
              const summary = getWhatChangedSummary();
              const details = selectedUpdate.details || {};
              const hasPreviousDateTime = details.previousDateTime != null;
              return (
                <div
                  className="update-details-summary"
                  style={{ borderLeftColor: getDetailsDialogTitleColor() }}
                >
                  <p className="update-details-summary__text">{summary.label}</p>
                  {(summary.type === 'date_time' || hasPreviousDateTime) && (
                    <div className="update-details-summary__from-to">
                      <span>From: {formatDateTime(details.previousDateTime)}</span>
                      <span>To: {formatDateTime(details.dateTime)}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="update-details-row">
              <span className="update-details-row__label">Client</span>
              <p className="update-details-row__value" style={{ textTransform: 'none' }}>
                {selectedUpdate?.details?.clientName || 'N/A'}
              </p>
            </div>

            <div className="update-details-row">
              <span className="update-details-row__label">Appointment Date & Time</span>
              <p className="update-details-row__value" style={{ textTransform: 'none' }}>
                {formatDateTime(selectedUpdate?.details?.dateTime)}
              </p>
            </div>

            {selectedUpdate?.details?.previousDateTime != null && (
              <div className="update-details-row">
                <span className="update-details-row__label">Previous Date & Time</span>
                <p className="update-details-row__value" style={{ textTransform: 'none' }}>
                  {formatDateTime(selectedUpdate.details.previousDateTime)}
                </p>
              </div>
            )}

            <div className="update-details-row">
              <span className="update-details-row__label">Service</span>
              <p className="update-details-row__value" style={{ textTransform: 'none' }}>
                {isResolvingService && getServiceDisplay(selectedUpdate) === 'N/A'
                  ? 'Loading service...'
                  : getServiceDisplay(selectedUpdate)}
              </p>
            </div>

            {isCancellationType() && (
              <div className="update-details-row">
                <span className="update-details-row__label">Cancellation Reason</span>
                <p className="update-details-row__value" style={{ textTransform: 'none' }}>
                  {selectedUpdate?.details?.cancelReason || 'No reason provided'}
                </p>
              </div>
            )}

            <div className="update-details-row">
              <span className="update-details-row__label">
                {isCancellationType() ? 'Cancelled By' : 'Updated By'}
              </span>
              <p className="update-details-row__value">
                {getActorDisplayName(
                  selectedUpdate,
                  selectedUpdate?.details?.updatedBy || selectedUpdate?.updatedBy
                )}
              </p>
            </div>

            <div className="update-details-row">
              <span className="update-details-row__label">
                {isCancellationType() ? 'Cancelled At' : 'Updated At'}
              </span>
              <p className="update-details-row__value" style={{ textTransform: 'none' }}>
                {formatDateTime(selectedUpdate?.timestamp)}
              </p>
            </div>
          </div>

          <footer className="update-details-dialog__actions">
            <button
              type="button"
              className="update-details-dialog__btn-close"
              onClick={() => setIsDetailsDialogOpen(false)}
            >
              Close
            </button>
          </footer>
        </div>
      </div>

    </div>
  );
};

export default RecentUpdatesCard;